import { useMemo } from "react";
import type { BossRevealEffect, Point } from "../types";
import { BossRevealParticles } from "./BossRevealParticles";

interface Props {
  effect: BossRevealEffect;
  anchor: Point;
  radiusPx: number;
  bounds: { left: number; top: number; width: number; height: number };
  projectionMask?: string;
  paused?: boolean;
}

const CRACK_PATHS = [
  "M50 50 L44 35 L47 28 L39 14 L41 2",
  "M50 50 L58 38 L56 29 L67 18 L72 4",
  "M50 50 L68 48 L74 42 L91 44 L99 39",
  "M50 50 L64 59 L75 58 L87 70 L99 74",
  "M50 50 L54 68 L61 74 L58 91 L64 99",
  "M50 50 L42 67 L43 77 L31 89 L27 99",
  "M50 50 L34 56 L27 64 L11 63 L1 70",
  "M50 50 L34 43 L25 45 L12 34 L1 31",
  "M50 50 L49 39 L54 33 L51 20",
  "M50 50 L61 53 L66 61 L80 64",
  "M50 50 L47 61 L39 67 L37 82",
  "M50 50 L39 49 L32 40 L18 39",
];

const SPARKS = Array.from({ length: 18 }, (_, index) => ({
  angle: index * (360 / 18) + (index % 3) * 7,
  distance: 42 + (index % 5) * 13,
  delay: (index % 6) * 110,
}));

function TableLegends({ effect }: { effect: BossRevealEffect }) {
  return <div className="boss-reveal-legends" aria-label={`${effect.title}${effect.subtitle ? `, ${effect.subtitle}` : ""}`}>
    {["north", "south", "west", "east"].map((edge) => <div className={`boss-reveal-legend ${edge}`} key={edge}>
      <small>{effect.subtitle || "THE ADVERSARY AWAKENS"}</small><strong>{effect.title}</strong>
    </div>)}
  </div>;
}

export function BossReveal({ effect, anchor, radiusPx, bounds, projectionMask, paused = false }: Props) {
  const elapsed = Math.max(0, Date.now() - effect.startedAt);
  const circleVariant = effect.circleVariant ?? "runic";
  const sparks = useMemo(() => SPARKS, []);
  const local = { x: anchor.x - bounds.left, y: anchor.y - bounds.top };
  const maskStyle = projectionMask ? { maskImage: projectionMask, WebkitMaskImage: projectionMask, maskSize: "100% 100%", WebkitMaskSize: "100% 100%" } : undefined;
  const style = {
    left: bounds.left, top: bounds.top, width: bounds.width, height: bounds.height,
    "--boss-x": `${local.x}px`, "--boss-y": `${local.y}px`, "--boss-radius": `${Math.max(44, radiusPx)}px`,
    "--boss-color": effect.color, "--boss-delay": `${-Math.min(elapsed, effect.durationMs - 1)}ms`,
    "--boss-duration": `${effect.durationMs}ms`, ...maskStyle,
  } as React.CSSProperties;
  return <div className={`boss-reveal circle-${circleVariant}${paused ? " paused" : ""}`} style={style} data-boss-reveal={effect.title} data-boss-reveal-id={effect.id} data-boss-circle-variant={circleVariant} aria-live="assertive">
    <div className="boss-reveal-darkness" />
    <div className="boss-reveal-shadow" />
    <BossRevealParticles effectId={effect.id} variant={circleVariant} color={effect.color} anchor={local} width={bounds.width} height={bounds.height} radius={Math.max(44, radiusPx)} startedAt={effect.startedAt} durationMs={effect.durationMs} paused={paused} />
    <svg className="boss-reveal-cracks" viewBox="0 0 100 100" aria-hidden="true">
      <defs><filter id={`boss-glow-${effect.id}`}><feGaussianBlur stdDeviation="1.2" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter></defs>
      <g filter={`url(#boss-glow-${effect.id})`}>{CRACK_PATHS.map((path, index) => <path d={path} key={path} style={{ "--crack-delay": `${index * 45}ms` } as React.CSSProperties} />)}</g>
    </svg>
    {circleVariant === "runic" ? <div className="boss-reveal-rune boss-reveal-circle"><i /><i /><i /><b>ᚱ</b><b>ᛉ</b><b>ᚨ</b><b>ᚦ</b></div> : null}
    {circleVariant === "void" ? <div className="boss-reveal-circle boss-reveal-void-circle"><i /><i /><i /><i /><b>ABYSS</b></div> : null}
    {circleVariant === "infernal" ? <div className="boss-reveal-circle boss-reveal-infernal-circle"><span>{Array.from({ length: 12 }, (_, index) => <i key={index} style={{ "--crown-angle": `${index * 30}deg` } as React.CSSProperties} />)}</span><b>ᛝ</b></div> : null}
    <div className="boss-reveal-vortex" aria-hidden="true">{sparks.map((spark, index) => <i key={index} style={{ "--spark-angle": `${spark.angle}deg`, "--spark-distance": `${spark.distance}px`, "--spark-delay": `${spark.delay}ms` } as React.CSSProperties} />)}</div>
    <div className="boss-reveal-impact"><i /><i /><i /></div>
    <TableLegends effect={effect} />
  </div>;
}
